import { useMemo } from "react";
import * as Icons from "lucide-react";
import { useCollection } from "@/hooks/useCollection";
import { text } from "@/lib/format";
import { custLabel } from "@/lib/customerIdentity";
import { labelOf } from "@/lib/fieldEnums";
import { cn } from "@/lib/utils";

// Lookups shared by the three Super CRM tabs (project, customer, pro, milestone names).
export function useCrmLookups() {
  const projects = useCollection("projects");
  const requests = useCollection("requests");
  const users = useCollection("users");
  const pros = useCollection("professionals");
  return useMemo(() => {
    const projectById = Object.fromEntries(projects.map((p) => [p.id, p]));
    const projectByNumber = Object.fromEntries(projects.filter((p) => p.project_number).map((p) => [String(p.project_number), p]));
    const reqById = Object.fromEntries(requests.map((r) => [r.id, r]));
    const userById = Object.fromEntries(users.map((u) => [u.id, u]));
    const proById = Object.fromEntries(pros.map((p) => [p.id, p]));
    const projectOf = (d) => projectById[d.project_id] || (d.project_number ? projectByNumber[String(d.project_number)] : null) || null;
    const projectLabel = (d) => {
      const p = projectOf(d);
      const num = d.project_number || p?.project_number;
      return { code: num ? `#${num}` : text(d.project_id, "—"), title: text(p?.title || (p?.services || []).join(", ") || d.title, "") };
    };
    const customerName = (d) => {
      const p = projectOf(d);
      const cid = d.customer_id || p?.customer_id;
      const req = reqById[p?.request_id || d.project_id || d.request_id];
      return custLabel(userById[cid], req) || (cid ? String(cid).slice(0, 10) : "—");
    };
    const proName = (id) => text(proById[id]?.full_name || proById[id]?.business_name, id ? String(id).slice(0, 10) : "—");
    return { projects, projectById, projectOf, projectLabel, customerName, proName, proById };
  }, [projects, requests, users, pros]);
}

export const ProjectCell = ({ d, L }) => {
  const { code, title } = L.projectLabel(d);
  return (
    <div className="min-w-0">
      <p className="text-sm font-medium text-foreground truncate max-w-[200px]">{title || code}</p>
      <p className="text-[11px] text-muted-foreground truncate max-w-[200px]"><span className="font-mono">{code}</span> · {L.customerName(d)}</p>
    </div>
  );
};

const TONE = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200", accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200", customer_approved: "bg-emerald-50 text-emerald-700 border-emerald-200", paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  in_progress: "bg-cyan-50 text-cyan-700 border-cyan-200", under_review: "bg-indigo-50 text-indigo-700 border-indigo-200", ready_for_review: "bg-indigo-50 text-indigo-700 border-indigo-200",
  sent_to_customer: "bg-sky-50 text-sky-700 border-sky-200", pending_customer_signature: "bg-amber-50 text-amber-700 border-amber-200",
  pending_pro_signature: "bg-amber-50 text-amber-700 border-amber-200", pending_brugo_signature: "bg-orange-50 text-orange-700 border-orange-200",
  change_requested: "bg-red-50 text-red-700 border-red-200", declined: "bg-red-50 text-red-700 border-red-200", disputed: "bg-red-50 text-red-700 border-red-200",
  critical: "bg-red-50 text-red-700 border-red-200", link_sent: "bg-indigo-50 text-indigo-700 border-indigo-200", ready_to_pay: "bg-amber-50 text-amber-700 border-amber-200",
};
export const Pill = ({ v, className = "" }) => (
  <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap", TONE[v] || "bg-slate-100 text-slate-600 border-slate-200", className)}>{labelOf(v)}</span>
);

// Customer → Pro(s) → Brugo signature trail.
export function SignatureTrail({ plan, L }) {
  const s = plan.signatures || {};
  const proKeys = (plan.hired_professional_ids?.length ? plan.hired_professional_ids : plan.hired_professional_uids) || [];
  const pros = s.pros || {};
  const proSigned = proKeys.length ? proKeys.filter((k) => pros[k]).length : (s.pro ? 1 : 0);
  const proTotal = proKeys.length || 1;
  const Step = ({ ok, label, title }) => (
    <span title={title} className={cn("inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold", ok ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-border bg-background text-muted-foreground")}>
      {ok ? <Icons.Check className="h-3 w-3" /> : <Icons.Circle className="h-2.5 w-2.5" />}{label}
    </span>
  );
  return (
    <div className="flex items-center gap-1" data-testid={`sig-${plan.id}`}>
      <Step ok={!!s.customer} label="Customer" title={s.customer ? "Customer signed" : "Customer not signed"} />
      <Icons.ChevronRight className="h-3 w-3 text-muted-foreground" />
      <Step ok={proSigned >= proTotal} label={`Pro ${proSigned}/${proTotal}`} title={proKeys.map((k) => `${L ? L.proName(k) : k}: ${pros[k] ? "signed" : "pending"}`).join("\n")} />
      <Icons.ChevronRight className="h-3 w-3 text-muted-foreground" />
      <Step ok={!!s.brugo} label="Brugo" title={s.brugo ? "Brugo signed" : "Brugo not signed"} />
    </div>
  );
}

export const Section = ({ icon, title, children, right }) => {
  const Icon = Icons[icon] || Icons.Circle;
  return (
    <div className="mt-5">
      <div className="flex items-center mb-2">
        <h4 className="font-heading font-semibold text-foreground flex items-center gap-2 text-sm"><Icon className="h-4 w-4 text-primary" /> {title}</h4>
        <div className="ml-auto">{right}</div>
      </div>
      <div className="rounded-lg border border-border p-3 space-y-2">{children}</div>
    </div>
  );
};
export const KV = ({ k, children }) => (
  <div className="flex items-start justify-between gap-3 text-sm">
    <span className="text-muted-foreground shrink-0 text-xs pt-0.5">{k}</span>
    <span className="text-foreground text-right min-w-0 break-words">{children}</span>
  </div>
);
